import React, { useEffect, useMemo, useState } from 'react';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { INK } from '../../Utils/styleTokens';
import {
  listCustomAlgorithms,
  uploadCustomAlgorithm,
  updateCustomAlgorithm,
  testCustomAlgorithm,
  deleteCustomAlgorithm,
} from '../api';

// Name validation mirrors the server-side rule exactly (masking worker's
// /api/v1/config/algorithms), same pattern as custom lookup lists.
const NAME_REGEX = /^[A-Za-z][A-Za-z0-9_]{2,63}$/;

const STATUS_STYLES = {
  pending: { background: '#FEF3C7', color: '#B45309' },
  active: { background: '#DCFCE7', color: '#15803D' },
  error: { background: '#FEE2E2', color: '#B91C1C' },
};

// Status chip: "Pending test" (never tested / not yet active), "Active" (active and the
// worker's class loaded), "Load error" (marked active but the worker failed to (re)load the
// class — tooltip surfaces loadError, e.g. after a masking-worker restart with a bad jar).
const StatusChip = ({ algorithm }) => {
  if (!algorithm.active) {
    return <Chip size='small' label='Pending test' style={STATUS_STYLES.pending} />;
  }
  if (algorithm.loaded) {
    return <Chip size='small' label='Active' style={STATUS_STYLES.active} />;
  }
  return (
    <Tooltip title={algorithm.loadError || 'Failed to load'}>
      <Chip size='small' label='Load error' style={STATUS_STYLES.error} />
    </Tooltip>
  );
};

const lastTestSummary = (algorithm) => {
  if (!algorithm.lastTestAt) return '—';
  let when = algorithm.lastTestAt;
  try {
    when = new Date(algorithm.lastTestAt).toLocaleString();
  } catch {
    // keep raw value
  }
  return algorithm.lastTestSuccess ? `Passed · ${when}` : `Failed · ${when}`;
};

// Custom Algorithms screen (Advanced Config). Lets admins upload a jar implementing the
// masking-service's MaskingAlgorithm interface, run a sample-text test against it, and — once
// the test succeeds — have it appear in every algorithm dropdown across the product, install-
// wide. Deleting an algorithm still referenced by an entity->algorithm mapping is blocked with
// a confirm-and-force dialog, same pattern as Custom Lookup Lists.
const CustomAlgorithmsScreen = ({ canEdit }) => {
  const [algorithms, setAlgorithms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [testTarget, setTestTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      const list = await listCustomAlgorithms();
      setAlgorithms(Array.isArray(list) ? list : []);
      setError(null);
    } catch (e) {
      setError(e?.body?.message || e?.message || 'Failed to load custom algorithms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const existingNames = useMemo(
    () => new Set(algorithms.map((a) => a.name).filter(Boolean)),
    [algorithms],
  );

  return (
    <div>
      <h1 className='adv-config-screen-title'>Custom Algorithms</h1>
      <p className='adv-config-screen-desc'>
        Upload a custom masking algorithm jar. It becomes available in every algorithm dropdown
        across the product, install-wide, once it passes a sample-text test below.
      </p>

      <div className='adv-config-card' style={{ background: '#F6F7F9' }}>
        <Typography variant='body2' style={{ fontWeight: 600, marginBottom: 6 }}>
          Implementor contract
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          The jar must contain a public class implementing{' '}
          <code>com.axistechnologyllc.datamasking.masking.MaskingAlgorithm</code> with a public
          no-arg constructor. Its <code>mask()</code> method must be thread-safe. The jar should
          be self-contained (shaded) — bundle any third-party dependencies the algorithm needs,
          since the worker classloader will not resolve them separately.
        </Typography>
      </div>

      {error && <div className='adv-config-msg-err' style={{ marginBottom: 16 }}>{error}</div>}

      {canEdit && (
        <UploadForm existingNames={existingNames} onUploaded={load} />
      )}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className='adv-config-card'>
          <table className='adv-config-table' data-testid='custom-algorithms-table'>
            <thead>
              <tr>
                <th>Name</th>
                <th>Class</th>
                <th>Jar</th>
                <th style={{ width: 120 }}>Status</th>
                <th>Last test</th>
                <th style={{ width: 230 }} />
              </tr>
            </thead>
            <tbody>
              {algorithms.map((a) => (
                <tr key={a.name} data-testid={`custom-algo-row-${a.name}`}>
                  <td>{a.name}</td>
                  <td style={{ fontSize: 12, color: INK[500] }}>{a.className}</td>
                  <td style={{ fontSize: 12, color: INK[500] }}>{a.jarFileName}</td>
                  <td><StatusChip algorithm={a} /></td>
                  <td style={{ fontSize: 12 }}>{lastTestSummary(a)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type='button'
                        className='adv-config-btn-secondary adv-config-btn'
                        data-testid={`custom-algo-test-${a.name}`}
                        disabled={!canEdit}
                        onClick={() => setTestTarget(a)}
                      >
                        Test
                      </button>
                      <button
                        type='button'
                        className='adv-config-btn-secondary adv-config-btn'
                        data-testid={`custom-algo-edit-${a.name}`}
                        disabled={!canEdit}
                        onClick={() => setEditTarget(a)}
                      >
                        Edit
                      </button>
                      <button
                        type='button'
                        className='adv-config-btn-secondary adv-config-btn'
                        data-testid={`custom-algo-delete-${a.name}`}
                        disabled={!canEdit}
                        onClick={() => setDeleteTarget(a.name)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {algorithms.length === 0 && (
                <tr><td colSpan={6} style={{ color: INK[500] }}>No custom algorithms uploaded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {testTarget && (
        <TestDialog
          algorithm={testTarget}
          onClose={() => setTestTarget(null)}
          onTested={load}
        />
      )}

      {editTarget && (
        <EditDialog
          algorithm={editTarget}
          existingNames={existingNames}
          onClose={() => setEditTarget(null)}
          onSaved={load}
        />
      )}

      {deleteTarget && (
        <DeleteDialog
          name={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={load}
        />
      )}
    </div>
  );
};

// Upload form: file + name + fully-qualified class name. The replace checkbox only appears
// once the typed name collides with an existing row (mirrors the task's ask to keep the
// control out of the way for the common "brand new algorithm" case).
const UploadForm = ({ existingNames, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [name, setName] = useState('');
  const [className, setClassName] = useState('');
  const [replace, setReplace] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [message, setMessage] = useState(null);

  const trimmedName = name.trim();
  const trimmedClassName = className.trim();
  const nameValid = NAME_REGEX.test(trimmedName);
  const nameInvalid = trimmedName !== '' && !nameValid;
  const isCollision = nameValid && existingNames.has(trimmedName);
  const canSubmit = !!file && nameValid && trimmedClassName !== '' && (!isCollision || replace);

  const resetError = () => {
    setSubmitError(null);
    setMessage(null);
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    resetError();
    setSubmitting(true);
    try {
      await uploadCustomAlgorithm({
        file,
        name: trimmedName,
        className: trimmedClassName,
        replace: isCollision && replace,
      });
      setMessage(`Uploaded "${trimmedName}". Run a test below to activate it.`);
      setFile(null);
      setFileInputKey((k) => k + 1);
      setName('');
      setClassName('');
      setReplace(false);
      if (onUploaded) onUploaded();
    } catch (e) {
      if (e?.status === 422) {
        setSubmitError({
          headline: e?.body?.error || e?.message || 'The jar was uploaded but the class failed to load.',
          kind: e?.body?.kind,
          stackSummary: e?.body?.stackSummary,
        });
      } else if (e?.status === 409) {
        setSubmitError({ headline: e?.body?.error || e?.body?.message || 'An algorithm with that name already exists.' });
      } else {
        setSubmitError({ headline: e?.body?.message || e?.message || 'Upload failed' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='adv-config-card'>
      <h2 style={{ fontSize: 15, margin: '0 0 12px' }}>Upload a custom algorithm</h2>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <TextField
          inputProps={{ 'data-testid': 'custom-algo-name' }}
          label='Algorithm name'
          placeholder='e.g. my_custom_hash'
          value={name}
          onChange={(e) => { setName(e.target.value); resetError(); }}
          error={nameInvalid}
          helperText={
            nameInvalid
              ? 'Must start with a letter and contain only letters, numbers, and underscores (3-64 characters).'
              : ' '
          }
          style={{ minWidth: 260 }}
          margin='dense'
        />
        <TextField
          inputProps={{ 'data-testid': 'custom-algo-classname' }}
          label='Fully-qualified class name'
          placeholder='com.example.masking.MyAlgorithm'
          value={className}
          onChange={(e) => { setClassName(e.target.value); resetError(); }}
          style={{ minWidth: 320 }}
          margin='dense'
        />
        <div style={{ marginTop: 20 }}>
          <input
            key={fileInputKey}
            data-testid='custom-algo-file'
            type='file'
            accept='.jar'
            onChange={(e) => { setFile(e.target.files?.[0] || null); resetError(); }}
          />
        </div>
      </div>

      {isCollision && (
        <FormControlLabel
          control={
            <Checkbox
              inputProps={{ 'data-testid': 'custom-algo-replace' }}
              checked={replace}
              onChange={(e) => setReplace(e.target.checked)}
            />
          }
          label={`An algorithm named "${trimmedName}" already exists — check to replace it.`}
          style={{ marginTop: 4, display: 'block' }}
        />
      )}

      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button
          data-testid='custom-algo-upload'
          variant='contained'
          color='primary'
          size='small'
          disabled={submitting || !canSubmit}
          onClick={handleSubmit}
        >
          {submitting ? 'Uploading…' : 'Upload'}
        </Button>
        {message && <span className='adv-config-msg-ok'>{message}</span>}
      </div>

      {submitError && (
        <div className='adv-config-msg-err' style={{ marginTop: 10 }} data-testid='custom-algo-upload-error'>
          <div>{submitError.headline}</div>
          {submitError.kind && (
            <div style={{ fontSize: 12, marginTop: 4 }} data-testid='custom-algo-upload-error-kind'>
              Kind: {submitError.kind}
            </div>
          )}
          {submitError.stackSummary && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12 }}>Stack summary</summary>
              <pre
                className='adv-config-mono'
                data-testid='custom-algo-upload-error-stack'
                style={{ maxHeight: 240, overflow: 'auto', marginTop: 6 }}
              >
                {submitError.stackSummary}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

// Per-row edit dialog: rename, change the implementing class, and/or replace the jar. A rename
// alone keeps the algorithm active (the server cascades the jar file, registry entry, aliases,
// and entity->algorithm mappings). Changing the class or jar resets the algorithm to "Pending
// test" — it must pass a test again before re-entering the algorithm dropdowns.
const EditDialog = ({ algorithm, existingNames, onClose, onSaved }) => {
  const [name, setName] = useState(algorithm.name);
  const [className, setClassName] = useState(algorithm.className || '');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const trimmedName = name.trim();
  const trimmedClassName = className.trim();
  const nameValid = NAME_REGEX.test(trimmedName);
  const nameInvalid = trimmedName !== '' && !nameValid;
  const renaming = nameValid && trimmedName !== algorithm.name;
  const isCollision = renaming && existingNames.has(trimmedName);
  const classChanged = trimmedClassName !== '' && trimmedClassName !== algorithm.className;
  const requiresRetest = classChanged || !!file;
  const changed = renaming || requiresRetest;
  const canSave = nameValid && trimmedClassName !== '' && changed && !isCollision;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setError(null);
    setSaving(true);
    try {
      await updateCustomAlgorithm(algorithm.name, {
        newName: renaming ? trimmedName : undefined,
        className: classChanged ? trimmedClassName : undefined,
        file: file || undefined,
      });
      if (onSaved) onSaved();
      onClose();
      return;
    } catch (e) {
      if (e?.status === 422) {
        setError({
          headline: e?.body?.error || e?.message || 'The class failed to load — nothing was changed.',
          kind: e?.body?.kind,
          stackSummary: e?.body?.stackSummary,
        });
      } else if (e?.status === 409) {
        setError({ headline: e?.body?.error || e?.body?.message || 'That name is already in use.' });
      } else {
        setError({ headline: e?.body?.error || e?.body?.message || e?.message || 'Update failed' });
      }
    }
    setSaving(false);
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth data-testid={`custom-algo-edit-dialog-${algorithm.name}`}>
      <DialogTitle>Edit {algorithm.name}</DialogTitle>
      <DialogContent>
        <TextField
          inputProps={{ 'data-testid': 'custom-algo-edit-name' }}
          label='Algorithm name'
          value={name}
          onChange={(e) => { setName(e.target.value); setError(null); }}
          error={nameInvalid || isCollision}
          helperText={
            nameInvalid
              ? 'Must start with a letter and contain only letters, numbers, and underscores (3-64 characters).'
              : isCollision
                ? `An algorithm named "${trimmedName}" already exists.`
                : renaming
                  ? 'Renaming also renames the jar, the default alias, and any entity mappings pointing at this algorithm.'
                  : ' '
          }
          fullWidth
          margin='dense'
        />
        <TextField
          inputProps={{ 'data-testid': 'custom-algo-edit-classname' }}
          label='Fully-qualified class name'
          value={className}
          onChange={(e) => { setClassName(e.target.value); setError(null); }}
          fullWidth
          margin='dense'
        />
        <div style={{ marginTop: 12 }}>
          <Typography variant='body2' style={{ marginBottom: 4 }}>
            Replacement jar (optional — leave empty to keep {algorithm.jarFileName})
          </Typography>
          <input
            data-testid='custom-algo-edit-file'
            type='file'
            accept='.jar'
            onChange={(e) => { setFile(e.target.files?.[0] || null); setError(null); }}
          />
        </div>

        {requiresRetest && (
          <div className='adv-config-msg-err' style={{ marginTop: 12 }} data-testid='custom-algo-edit-retest-note'>
            Changing the class or jar resets this algorithm to “Pending test” — it will leave the
            algorithm dropdowns until it passes a test again. Its aliases are kept and come back
            with it.
          </div>
        )}

        {error && (
          <div className='adv-config-msg-err' style={{ marginTop: 12 }} data-testid='custom-algo-edit-error'>
            <div>{error.headline}</div>
            {error.kind && (
              <div style={{ fontSize: 12, marginTop: 4 }}>Kind: {error.kind}</div>
            )}
            {error.stackSummary && (
              <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12 }}>Stack summary</summary>
                <pre className='adv-config-mono' style={{ maxHeight: 240, overflow: 'auto', marginTop: 6 }}>
                  {error.stackSummary}
                </pre>
              </details>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          data-testid='custom-algo-edit-save'
          variant='contained'
          color='primary'
          disabled={saving || !canSave}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Per-row test dialog. Re-testable any time; a success response means the worker has now
// registered the algorithm (it will show up in every algorithm dropdown from this point on).
const TestDialog = ({ algorithm, onClose, onTested }) => {
  const [sampleText, setSampleText] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [configText, setConfigText] = useState('');
  const [configError, setConfigError] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleRun = async () => {
    setError(null);
    setResult(null);
    let config;
    if (showConfig && configText.trim()) {
      try {
        config = JSON.parse(configText);
      } catch {
        setConfigError('Config must be valid JSON.');
        return;
      }
    }
    setConfigError(null);
    setRunning(true);
    try {
      const res = await testCustomAlgorithm(algorithm.name, { sampleText, config });
      setResult(res);
      if (onTested) onTested();
    } catch (e) {
      setError(e?.body?.error || e?.body?.message || e?.message || 'Test failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth data-testid={`custom-algo-test-dialog-${algorithm.name}`}>
      <DialogTitle>Test {algorithm.name}</DialogTitle>
      <DialogContent>
        <TextField
          inputProps={{ 'data-testid': 'custom-algo-test-sample' }}
          label='Sample text'
          value={sampleText}
          onChange={(e) => setSampleText(e.target.value)}
          fullWidth
          margin='dense'
        />

        <Accordion expanded={showConfig} onChange={(e, exp) => setShowConfig(exp)} style={{ marginTop: 12 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid='custom-algo-test-config-toggle'>
            <Typography variant='body2'>Advanced config (optional JSON)</Typography>
          </AccordionSummary>
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <TextField
              inputProps={{ 'data-testid': 'custom-algo-test-config' }}
              value={configText}
              onChange={(e) => { setConfigText(e.target.value); setConfigError(null); }}
              multiline
              rows={4}
              fullWidth
              variant='outlined'
              placeholder='{"key": "value"}'
              error={!!configError}
              helperText={configError || ' '}
            />
          </AccordionDetails>
        </Accordion>

        {result && (
          <div style={{ marginTop: 14 }} data-testid='custom-algo-test-result'>
            {result.success ? (
              <>
                <div className='adv-config-msg-ok'>
                  {result.defaultAlias
                    ? `Registered as alias '${result.defaultAlias}' — manage aliases in the Algorithm Aliases tab.`
                    : 'Registered — now available in all algorithm dropdowns.'}
                </div>
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  <strong>Input:</strong> {result.input}
                </div>
                <div style={{ fontSize: 13 }}>
                  <strong>Output:</strong>{' '}
                  {result.output != null ? result.output : <em>null</em>}
                </div>
                {result.warning && (
                  <div className='adv-config-msg-err' style={{ marginTop: 6 }}>{result.warning}</div>
                )}
                {result.durationMs != null && (
                  <div style={{ fontSize: 12, color: INK[500], marginTop: 6 }}>{result.durationMs} ms</div>
                )}
              </>
            ) : (
              <>
                <div className='adv-config-msg-err'>{result.error || 'Test failed.'}</div>
                {result.stackSummary && (
                  <pre className='adv-config-mono' style={{ maxHeight: 240, overflow: 'auto', marginTop: 8 }}>
                    {result.stackSummary}
                  </pre>
                )}
              </>
            )}
          </div>
        )}
        {error && <div className='adv-config-msg-err' style={{ marginTop: 12 }}>{error}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          data-testid='custom-algo-test-run'
          variant='contained'
          color='primary'
          disabled={running}
          onClick={handleRun}
        >
          {running ? 'Running…' : 'Run test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete confirm dialog. First attempt runs with force=false; a 409 with referencedBy swaps
// the dialog into a force-confirm state (same shape as LookupListsScreen's confirm dialog).
const DeleteDialog = ({ name, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [referencedBy, setReferencedBy] = useState(null);

  const doDelete = async (force) => {
    setDeleting(true);
    setError(null);
    try {
      await deleteCustomAlgorithm(name, force);
      if (onDeleted) onDeleted();
      // Unmounts this dialog — no further state updates below on success, since setting
      // state on it afterwards would warn about updating an unmounted component.
      onClose();
      return;
    } catch (e) {
      const refs = e?.body && Array.isArray(e.body.referencedBy) ? e.body.referencedBy : null;
      if (e?.status === 409 && refs && refs.length > 0 && !force) {
        setReferencedBy(refs);
      } else {
        setError(e?.body?.message || e?.message || 'Delete failed');
      }
    }
    setDeleting(false);
  };

  return (
    <Dialog open onClose={onClose} data-testid={`custom-algo-delete-dialog-${name}`}>
      <DialogTitle>{referencedBy ? 'Algorithm in use' : `Delete ${name}?`}</DialogTitle>
      <DialogContent>
        {referencedBy ? (
          <>
            <Typography gutterBottom>
              This algorithm is still referenced by the following entity → algorithm mappings.
              Deleting will leave them pointing at an algorithm that no longer exists unless you
              force the delete.
            </Typography>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {referencedBy.map((ref) => (
                <li key={ref} style={{ fontSize: 13 }}>{ref}</li>
              ))}
            </ul>
          </>
        ) : (
          <Typography>
            {`Delete the algorithm "${name}"? Any aliases created for it will be removed as well. This cannot be undone.`}
          </Typography>
        )}
        {error && <div className='adv-config-msg-err' style={{ marginTop: 10 }}>{error}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button
          data-testid={referencedBy ? `custom-algo-force-delete-${name}` : `custom-algo-confirm-delete-${name}`}
          color='secondary'
          variant='contained'
          disabled={deleting}
          onClick={() => doDelete(!!referencedBy)}
        >
          {referencedBy ? 'Force delete' : (deleting ? 'Deleting…' : 'Delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomAlgorithmsScreen;
